import { apiFetch } from './client.js';

/**
 * Money that has arrived at a partner and has not been given to anyone yet.
 *
 * Every call here either releases customer money or refuses it, so every write
 * carries a reason and the API keeps it against the payment.
 */

export type InboundPaymentStatus =
  | 'PENDING_REVIEW'
  | 'APPROVING'
  | 'CORRECTING_FEE'
  | 'REVERSING'
  | 'APPROVED'
  | 'REJECTED'
  | 'UNATTRIBUTED';

/** The filter the queue is read with. AWAITING is everything still undecided. */
export type InboundQueueFilter =
  | 'AWAITING'
  | 'PENDING_REVIEW'
  | 'UNATTRIBUTED'
  | 'APPROVED'
  | 'REJECTED';

export interface InboundPayment {
  id: string;
  provider: string;
  operationId: string;
  customerId: string | null;
  accountId: string | null;
  /**
   * Who the money is for. Null until somebody is attributed, which is its own
   * state rather than a blank.
   */
  recipientEmail: string | null;
  /** The account holder, as they are actually called. An email is a login. */
  recipientName: string | null;
  /** The IBAN the money was sent to — which account, not just which person. */
  beneficiaryAccount: string | null;
  /** What arrived, in minor units. */
  amountCents: string;
  currency: string;
  /** Priced when it was found; re-quoted at the moment of approval. */
  feeCents: string;
  /** amountCents - feeCents: what the customer actually receives. */
  netCents: string;
  /** The free text the remitter typed — the only link to a customer. */
  paymentDetails: string | null;
  payerName: string | null;
  /** Masked by the API. The full number is in the partner statement. */
  payerAccount: string | null;
  valueDate: string | null;
  status: InboundPaymentStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewReason: string | null;
  createdAt: string;
  /** The partner's own word for this payment, as last seen. */
  providerStatus: string | null;
  /** The monitoring provider's own word, e.g. "CONFIRMED". */
  screeningStatus: string | null;
  /** Overall risk on the PROVIDER's scale — not a percentage. */
  screeningScore: number | null;
  /** Rules that flagged, each with a reason and its own maximum. */
  screeningFlagged: ReadonlyArray<{
    rule?: string;
    reason?: string;
    score?: number;
    maxScore?: number;
  }>;
  /** Null means never screened, which is not the same as screened and clean. */
  screenedAt: string | null;
  /** Why a screening could not be obtained, when one could not be. */
  screeningDetail: string | null;
  /**
   * The partner says it did not deliver the money.
   *
   * Releasing must not be offered: it would credit the customer out of our own
   * funds against a transfer that does not exist. Decided by the API so the
   * console and the gate can never disagree.
   */
  providerRefused: boolean;
}

export interface InboundPaymentDetail extends InboundPayment {
  /** The decision is in the comment thread; the reviewed fields would repeat it (DEV-95). */
  decisionOnFile?: boolean;
  direction: string | null;
  serviceType: string | null;
  beneficiaryAccount: string | null;
  endToEndId: string | null;
  instructionId: string | null;
  purposeCode: string | null;
  payerCountry: string | null;
  senderBankName: string | null;
  senderBankBic: string | null;
  senderBankCountry: string | null;
  /** What the reviewer established about the sender (DEV-96). */
  payerType?: 'PERSON' | 'COMPANY' | null;
  payerAddress?: string | null;
  payerRegistrationNumber?: string | null;
  payerEditedBy?: string | null;
  payerEditedAt?: string | null;
  providerPayload: Readonly<Record<string, unknown>> | null;
  providerPayloadSource?: string | null;
  providerPayloadAt?: string | null;
  providerParentId?: string | null;
  releaseBlockedReason?: string | null;
}

export interface ApprovalResult {
  creditedCents: string;
  feeCents: string;
  netCents: string;
}

/** A payment somebody typed in from a bank's own screen. */
export interface ManualPaymentInput {
  /** The mechanic of the account it reached, e.g. MANUAL_SHARED. */
  provider: string;
  customerId: string;
  accountId: string;
  amountCents: number;
  currency: string;
  payerName: string;
  paymentDetails: string;
  /** The bank's own reference. The only thing that catches a double entry. */
  bankReference: string;
  valueDate: string;
}

/** What happened the last time Norvix asked a partner for arriving money. */
export interface InboundSource {
  /** When the last pass ran. */
  at: string;
  summary: {
    provider: string;
    state: 'DISABLED' | 'UNUSABLE' | 'READ';
    reason?: string;
    accountsQueried: number;
    accountsRead: number;
    transactionsRead: number;
    unreadable: number;
    outcomes: Record<string, number>;
  };
}

export interface CommissionChange {
  feeCents: string;
  expectedFeeCents: string;
  expectedRevision: number;
  reason: string;
}
export interface IncomingCommission {
  status: string;
  feeCents: string;
  amountCents: string;
  currency: string;
  editRevision: number;
  overrideFeeCents: string | null;
  verificationError: string | null;
  pendingOperationId: string | null;
  posted: null | { feeAccountId: string; originalFeeCents: string; feeCents: string; revision: number; reversed: boolean;
    changes: Array<{ operationId: string; beforeCents: string; afterCents: string; officerId: string; reason: string; createdAt: string }> };
  history: Array<{ id: string; kind: string; status: string; officerId: string; reason: string; createdAt: string; completedAt: string | null;
    request: null | { expectedFeeCents: string; feeCents: string; expectedRevision: number } }>;
}

/** The sender details a reviewer may correct; a field left out keeps its value. */
export interface PayerEdit {
  reason: string;
  payerType: 'PERSON' | 'COMPANY' | null;
  payerCountry: string;
  payerAddress: string | null;
  payerRegistrationNumber: string | null;
  senderBankName: string | null;
  senderBankBic: string | null;
  senderBankCountry: string | null;
}

/** One sanctions run on the sender or a sender company's UBO (DEV-96). */
export interface CounterpartyScreening {
  id: string;
  role: 'PAYER' | 'UBO';
  entityType: 'PERSON' | 'COMPANY';
  name: string;
  country: string;
  registrationNumber: string;
  dateOfBirth: string;
  profileId: string;
  providerId: string;
  parentProfileId: string;
  reusedProfile: boolean;
  /** IN_PROGRESS, CLEAR, MATCHES_FOUND or FAILED. */
  state: string;
  matchCount: number;
  requestId: number;
  providerCheckedAt: string;
  detail: string;
  requestedBy: string;
  requestedAt: string;
  completedAt: string;
}

export interface CounterpartyScreeningRequest {
  role: 'PAYER' | 'UBO';
  entityType: 'PERSON' | 'COMPANY';
  name: string;
  country: string;
  registrationNumber?: string;
  dateOfBirth?: string;
  address?: string;
  parentScreeningId?: string;
}

export const inboundPaymentsApi = {
  editPayer: (id: string, edit: PayerEdit): Promise<InboundPaymentDetail> =>
    apiFetch<InboundPaymentDetail>(`/admin/inbound-payments/${id}/payer`, { method: 'PATCH', body: edit }),

  counterpartyScreenings: (id: string): Promise<CounterpartyScreening[]> =>
    apiFetch<CounterpartyScreening[]>(`/admin/inbound-payments/${id}/counterparty-screenings`),

  screenCounterparty: (id: string, input: CounterpartyScreeningRequest): Promise<CounterpartyScreening> =>
    apiFetch<CounterpartyScreening>(`/admin/inbound-payments/${id}/counterparty-screenings`, { method: 'POST', body: input }),

  source: (): Promise<InboundSource | null> =>
    apiFetch<InboundSource | null>('/admin/inbound-payments/source'),

  list: (status: InboundQueueFilter = 'AWAITING'): Promise<InboundPayment[]> =>
    apiFetch<InboundPayment[]>(`/admin/inbound-payments?status=${status}`),

  get: (id: string): Promise<InboundPaymentDetail> =>
    apiFetch<InboundPaymentDetail>(`/admin/inbound-payments/${id}`),

  approve: (id: string, reason: string): Promise<ApprovalResult> =>
    apiFetch<ApprovalResult>(`/admin/inbound-payments/${id}/approve`, {
      method: 'POST',
      body: { reason },
    }),

  reject: (id: string, reason: string): Promise<{ rejected: boolean }> =>
    apiFetch<{ rejected: boolean }>(`/admin/inbound-payments/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * Take back money credited for a payment the partner never delivered.
   *
   * The only call here that makes a customer's balance go down.
   */
  reverse: (
    id: string,
    reason: string,
  ): Promise<{ reversedCents: string; feeRefundedCents: string; balanceWarning: boolean }> =>
    apiFetch(`/admin/inbound-payments/${id}/reverse`, { method: 'POST', body: { reason } }),

  /** Write a note on this payment. Attributed, dated, and never editable. */
  addNote: (id: string, body: string): Promise<{ recorded: boolean }> =>
    apiFetch(`/admin/inbound-payments/${id}/notes`, { method: 'POST', body: { body } }),

  /**
   * Correct a fee that was charged wrongly.
   *
   * Posts the difference as its own entry; the original charge is never edited.
   */
  commission: (id: string): Promise<IncomingCommission> => apiFetch(`/admin/inbound-payments/${id}/commission`),

  editFee: (id: string, input: CommissionChange, operationId: string): Promise<IncomingCommission> =>
    apiFetch(`/admin/inbound-payments/${id}/edit-fee`, { method: 'POST', headers: { 'Idempotency-Key': operationId }, body: input }),

  resumeCommission: (id: string, operationId: string): Promise<IncomingCommission> =>
    apiFetch(`/admin/inbound-payments/${id}/commission/${operationId}/resume`, { method: 'POST' }),

  correctFee: (id: string, input: CommissionChange, operationId: string): Promise<{ chargedBefore: string; chargedNow: string; adjustmentCents: string }> =>
    apiFetch(`/admin/inbound-payments/${id}/correct-fee`, {
      method: 'POST',
      headers: { 'Idempotency-Key': operationId }, body: input,
    }),

  /**
   * Record a payment that arrived at a bank we operate by hand.
   *
   * Files it into the review queue. It credits nobody: releasing it is a
   * separate decision, taken by somebody else, on the same screen as every
   * other incoming payment.
   */
  recordManual: (input: ManualPaymentInput): Promise<InboundPayment> =>
    apiFetch<InboundPayment>('/admin/inbound-payments/manual', { method: 'POST', body: input }),

  attribute: (
    id: string,
    input: { accountId: string; customerId: string; reason: string },
  ): Promise<{ attributed: boolean }> =>
    apiFetch<{ attributed: boolean }>(`/admin/inbound-payments/${id}/attribute`, {
      method: 'POST',
      body: input,
    }),
};
